import { useState } from "react";
import { Modal, ModalBody, ModalButton, ModalFooter, ModalHeader } from "baseui/modal";
import { FormControl } from "baseui/form-control";
import { Input } from "baseui/input";
import { useCreateSubscriptionMutation } from "../../../services/resourceGroups/subscriptionApi";
import { useReportMutationState } from "../../../hooks/useReportMutationState";
import Joi from "joi";
import { LabelMedium } from "baseui/typography";
import { StyledDivider } from "baseui/divider";
import { Select } from "baseui/select";
import { FormattedMessage } from "react-intl";

const CreateDialog = ({ isOpen, setIsOpen }) => {
  const [state, setState] = useState<any>({
    name: undefined,
    stripeId: undefined,
    adminMail: undefined,
    adminDisplayName: undefined,
    pricingModel: [],
  });
  const [create, createResult] = useCreateSubscriptionMutation();
  useReportMutationState(createResult, () => close());

  const close = () => setIsOpen(false);
  const add = async () =>
    await create({
      name: state.name,
      stripeId: state.stripeId,
      adminMail: state.adminMail,
      adminDisplayName: state.adminDisplayName,
      pricingModel: state.pricingModel[0].id,
    });

  const nameError = Joi.string().min(5).validate(state.name).error;
  const stripeIdError = Joi.string().min(15).validate(state.stripeId).error;
  const mailError = Joi.string()
    .email({ tlds: { allow: false } })
    .validate(state.adminMail).error;
  const pricingModelError = state.pricingModel.length !== 1;

  const allowSubmit =
    state.name && state.stripeId && state.adminMail && !mailError && !nameError && !stripeIdError && !pricingModelError;

  const setName = (newVal) => {
    setState((prevState) => ({ ...prevState, name: newVal }));
  };
  const setStripeId = (newVal) => {
    setState((prevState) => ({ ...prevState, stripeId: newVal }));
  };
  const setAdminMail = (newVal) => {
    setState((prevState) => ({ ...prevState, adminMail: newVal }));
  };
  const setAdminDisplayName = (newVal) => {
    setState((prevState) => ({ ...prevState, adminDisplayName: newVal }));
  };
  const setPricingModel = (newVal) => {
    setState((prevState) => ({ ...prevState, pricingModel: newVal }));
  };
  return (
    <>
      <Modal onClose={close} isOpen={isOpen}>
        <ModalHeader>Add new Subscription</ModalHeader>
        <ModalBody>
          <FormControl
            error={nameError ? nameError.message : null}
            label={() => "Name"}
            caption={() => "Enter a descriptive name for the Subscription"}>
            <Input
              value={state.name}
              onChange={(e) => {
                setName(e.target.value);
              }}
              placeholder="Enter name ..."
              clearable
              clearOnEscape
            />
          </FormControl>
          <FormControl
            error={stripeIdError ? stripeIdError.message : null}
            label={() => "Stripe Id"}
            caption={() => "Enter the Stripe Subscription id"}>
            <Input
              value={state.stripeId}
              onChange={(e) => {
                setStripeId(e.target.value);
              }}
              placeholder="sub_****"
              clearable
              clearOnEscape
            />
          </FormControl>
          <FormControl label={() => "Pricing Model"}>
            <Select
              options={pricingModels}
              value={state.pricingModel}
              placeholder="Pricing Model"
              onChange={(params) => {
                setPricingModel(params.value);
              }}
            />
          </FormControl>
          <StyledDivider />
          <LabelMedium marginBottom={"10px"}>Administrator</LabelMedium>
          <FormControl
            error={mailError ? mailError.message : null}
            label={() => "Mail"}
            caption={() => "Enter admin e-mail"}>
            <Input
              value={state.adminMail}
              onChange={(e) => {
                setAdminMail(e.target.value);
              }}
              placeholder="max.mustermann@ag.com"
              clearable
              clearOnEscape
            />
          </FormControl>
          <FormControl label={() => "Display Name"} caption={() => "Admin user display name"}>
            <Input
              value={state.adminDisplayName}
              onChange={(e) => {
                setAdminDisplayName(e.target.value);
              }}
              placeholder="Max Mustermann"
              clearable
              clearOnEscape
            />
          </FormControl>
        </ModalBody>
        <ModalFooter>
          <ModalButton kind="tertiary" onClick={close}>
            <FormattedMessage id="cancel" defaultMessage={"Cancel"} />
          </ModalButton>
          <ModalButton disabled={!allowSubmit} isLoading={createResult.isLoading} onClick={add}>
            <FormattedMessage id="ok" defaultMessage={"Ok"} />
          </ModalButton>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default CreateDialog;

const pricingModels = [
  { id: "per_m2_2024_11", label: "per m²" },
  { id: "06.2024", label: "per Devices and Users" },
];
